import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import BottomNav from "../components/BottomNav";
import { ShieldAlert, MapPin, Clock, Radar, Battery, Navigation, Heart, ChevronRight } from "lucide-react";
import { openDirections } from "../lib/directions";
import { isFamilyContact } from "../lib/colors";
import ParentChildInvite from "../components/ParentChildInvite";
import IdentityBadge from "../components/IdentityBadge";

export default function Family() {
  const navigate = useNavigate();
  const [family, setFamily] = useState([]);
  const [history, setHistory] = useState({});
  const [checkins, setCheckins] = useState({});

  useEffect(() => {
    (async () => {
      const r = await api.get("/contacts");
      // Every contact marked as favorite/family (or child/parent) appears here automatically
      const fam = (r.data || []).filter(isFamilyContact);
      setFamily(fam);
      const hist = {}; const ci = {};
      await Promise.all(fam.map(async (k) => {
        try {
          const h = await api.get(`/locations/history/${k.user_id}`, { params: { limit: 20 } });
          hist[k.user_id] = h.data || [];
        } catch { hist[k.user_id] = []; }
        try {
          const c = await api.get("/checkins", { params: { user_id: k.user_id }});
          ci[k.user_id] = c.data || [];
        } catch { ci[k.user_id] = []; }
      }));
      setHistory(hist); setCheckins(ci);
    })();
  }, []);

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-50 pb-28">
      <header className="px-6 pt-10 pb-6">
        <div className="label-eyebrow flex items-center gap-2"><Heart size={12} className="text-[#FF2D55]"/> Favorites & family</div>
        <h1 className="font-display font-black text-4xl tracking-tighter mt-1">Family</h1>
        <p className="text-zinc-500 mt-2 text-sm">Every contact you've marked with a <Heart size={12} className="inline text-[#FF2D55]" fill="#FF2D55"/> appears here — with live tracking, history & check-ins.</p>
      </header>

      <div className="px-6 mb-4">
        <ParentChildInvite />
      </div>

      {family.length === 0 ? (
        <div className="mx-6 p-8 rounded-2xl border border-white/5 text-center text-zinc-500">
          No favorites yet. Tap the <Heart size={12} className="inline text-[#FF2D55]"/> on any contact in{" "}
          <Link to="/contacts" className="text-[#007AFF] font-semibold">Contacts</Link> to add them here.
        </div>
      ) : (
        <div className="px-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
          {family.map((k) => (
            <div key={k.user_id} className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
              <div className="p-5 flex items-center gap-3 border-b border-white/5">
                <button
                  data-testid={`family-open-${k.user_id}`}
                  onClick={() => navigate(`/dashboard?focus=${encodeURIComponent(k.user_id)}`)}
                  className="flex items-center gap-3 flex-1 min-w-0 text-left"
                >
                  <div className="size-12 rounded-full bg-zinc-800 overflow-hidden grid place-items-center font-display font-bold">
                    {k.picture ? <img src={k.picture} alt="" className="w-full h-full object-cover" /> : (k.name||"?").slice(0,1)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-bold text-lg truncate flex items-center gap-1.5">
                      <IdentityBadge contact={k} size={20} />
                      {k.display_name || k.name}
                      <ChevronRight size={14} className="text-zinc-500"/>
                    </div>
                    <div className="text-xs text-zinc-500 truncate capitalize">{k.relation} · {k.email}</div>
                  </div>
                </button>
                <Link
                  to={`/find/${k.user_id}`}
                  data-testid={`family-find-${k.user_id}`}
                  className="inline-flex items-center gap-1.5 bg-[#007AFF] hover:bg-[#005ec4] text-white font-bold text-xs px-3 py-2 rounded-full"
                >
                  <Radar size={14}/> Find
                </Link>
                {k.latest && (
                  <button
                    data-testid={`family-navigate-${k.user_id}`}
                    onClick={() => openDirections({
                      lat: k.latest.lat, lng: k.latest.lng,
                      label: k.name, mode: "driving",
                    })}
                    className="inline-flex items-center gap-1.5 border border-white/10 hover:bg-white/5 font-bold text-xs px-3 py-2 rounded-full"
                  >
                    <Navigation size={14}/> Go
                  </button>
                )}
              </div>

              <div className="grid grid-cols-2 gap-px bg-white/5">
                <div className="bg-zinc-950 p-4">
                  <div className="label-eyebrow flex items-center gap-1.5"><MapPin size={10}/> Last seen</div>
                  <div className="mt-2 font-display font-bold text-xl">
                    {k.distance_miles == null ? "—" :
                      k.distance_miles < 0.19 ? `${Math.round(k.distance_miles*5280)} ft`
                      : `${k.distance_miles.toFixed(2)} mi`}
                  </div>
                  <div className="text-xs text-zinc-500 mt-1 font-mono">
                    {k.latest ? new Date(k.latest.ts).toLocaleTimeString() : "no signal"}
                  </div>
                </div>
                <div className="bg-zinc-950 p-4">
                  <div className="label-eyebrow flex items-center gap-1.5"><Battery size={10}/> Battery</div>
                  <div className="mt-2 font-display font-bold text-xl font-mono">
                    {k.latest?.battery != null ? `${k.latest.battery}%` : "—"}
                  </div>
                  <div className="text-xs text-zinc-500 mt-1">{k.share_location ? "Sharing" : "Paused"}</div>
                </div>
              </div>

              <div className="p-5">
                <div className="label-eyebrow flex items-center gap-1.5 mb-3"><Clock size={10}/> Recent check-ins</div>
                <div className="space-y-2 max-h-44 overflow-y-auto">
                  {(checkins[k.user_id] || []).length === 0 && (
                    <div className="text-sm text-zinc-600">No check-ins yet.</div>
                  )}
                  {(checkins[k.user_id] || []).slice(0, 5).map((c) => (
                    <div key={c.checkin_id} className="text-sm flex items-start gap-2">
                      <span className="size-1.5 rounded-full bg-[#34C759] mt-2"/>
                      <div className="flex-1">
                        <div className="text-zinc-300">{c.note || "Checked in"}</div>
                        <div className="text-xs text-zinc-600 font-mono">{new Date(c.ts).toLocaleString()}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="p-5 border-t border-white/5">
                <div className="label-eyebrow mb-3">Location history</div>
                <div className="space-y-1.5 max-h-44 overflow-y-auto">
                  {(history[k.user_id] || []).length === 0 && (
                    <div className="text-sm text-zinc-600">No history yet.</div>
                  )}
                  {(history[k.user_id] || []).slice(0, 8).map((h, i) => (
                    <div key={i} className="font-mono text-xs text-zinc-400 flex items-center gap-2">
                      <span className="text-zinc-600">{new Date(h.ts).toLocaleTimeString()}</span>
                      <span>{h.lat.toFixed(4)}, {h.lng.toFixed(4)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <BottomNav />
    </div>
  );
}
